/**
 * Screen 1h — analysis module.
 *
 * Two facilities behind one tab, and *one* tab on purpose:
 *
 *   Deterministik — the six-stage workflow, each stage showing its method,
 *   parameters, and whether its inputs were actually present. It lives in the
 *   left rail.
 *
 *   Multi-mineral — component endpoints, tool weights and constraints for the
 *   least-squares solver, with the stacked volumetric track, the incoherence
 *   curve, and the measured-vs-reconstructed QC overlay. It lives in the
 *   centre column and the right rail.
 *
 * Keduanya satu layout dan selalu tergambar bersama-sama, jadi dulu keduanya
 * berdiri sebagai dua segmen yang menggambar layar yang sama persis: diadu
 * piksel demi piksel, satu-satunya yang berganti adalah sorotan pemilihnya
 * sendiri. Sebuah pemilih yang menjanjikan perpindahan lalu tidak memindahkan
 * apa pun bukan sekadar mubazir — ia membuat orang mengira ada isi yang belum
 * termuat. Dan selama `mode` masih dipercaya menyimpan "yang mana yang sedang
 * dikerjakan", ia juga sempat memutar tombol yang tidak ditekan.
 *
 * Membandingkan keduanya tetap ada tempatnya, dan bukan di sini: tab Lumping
 * punya sumber `compare` yang menjajarkan angka keduanya pada zona yang sama.
 *
 * Tab-tab lain adalah fasilitas yang memang berbeda isinya: Batch menjalankan
 * alur deterministik atas banyak sumur sekaligus, Lumping menjumlahkan
 * hasilnya, dan Rock typing memisahkan jenis batuan dari φ dan k.
 */

import { Fragment, useMemo, useState } from "react";
import { api, unpage, type Page } from "../api";
import { canCompute, canWrite } from "../auth";
import { Icon } from "../components/icons";
import { RailCollapseButton, RailStrip, type RailTabItem } from "../components/RailTabs";
import { Resizer } from "../components/Resizer";
import { CutoffDialog } from "../components/CutoffTool";
import { PermDialog } from "../components/PermTool";
import { PhieDialog } from "../components/PhieTool";
import { PhitDialog } from "../components/PhitTool";
import { PointTestDialog } from "../components/PointTest";
import { SwDialog } from "../components/SwTool";
import { VshDialog } from "../components/VshTool";
import {
  Button,
  Checkbox,
  Dropdown,
  Empty,
  KeyValue,
  Loading,
  MethodButton,
  Notice,
  NumberField,
  Panel,
  Segmented,
  StatusDot,
  useToast,
} from "../components/ui";
import CurveInspector from "../components/CurveInspector";
import RockTyping from "../components/RockTyping";
import { BoxZoomOverlay, DepthGutter } from "../components/LogTracks";
import { kinOptions, STAGE_CURVES } from "../lib/curvekin";
import { defaultCurveColor } from "../lib/tracklayout";
import { ScreeningTable } from "../components/DataScope";
import { useAsync, useStored } from "../hooks";
import { DeterministicQc, DeterministicResults } from "../components/DeterministicResults";
import { MineralComponentsDialog } from "../components/MineralTool";
import { MultiminStages, multiminStages } from "../components/MultiminWorkflow";
import { depthTickCount } from "../lib/depthscale";
import {
  DEFAULT_EQUATION,
  EQUATION_HINT,
  EQUATION_LABEL,
  describeBounds,
  incoherenceTrace,
  rmsResidual,
} from "../lib/multimin";
import { useDepthSheet } from "../lib/depthsheet";
import type {
  AnalysisRun,
  LumpGroup,
  LumpRow,
  LumpingPayload,
  MineralComponent,
  MineralModel,
  ParameterSet,
  ParameterValues,
  Project,
  ScreenedWell,
  Screening,
  Well,
  WorkflowStep,
  ZoneSummary,
} from "../types";

/**
 * Fasilitas mana yang dibuka.
 *
 * `deterministic` dan `multimin` dulu satu tab bernama `interpret`, dan di
 * dalamnya alur deterministik hanya memiliki rel kirinya: kolom tengah dan rel
 * QC seluruhnya milik penyelesai multi-mineral. Dua tafsiran yang menjawab
 * pertanyaan berbeda dari log yang sama karena itu terbaca sebagai satu
 * tafsiran dengan dua bagian — dan yang deterministik kehilangan tempat untuk
 * mencetak ringkasan zonanya sendiri, yaitu angka yang justru menjadi alasan ia
 * dijalankan.
 */
type Mode = "deterministic" | "multimin" | "batch" | "lumping" | "rocktype";

/** Pekerjaan yang menulis, masing-masing dengan tombolnya sendiri. */
type Job = "workflow" | "stage" | "multimin" | "mineral" | "batch";

/** Tahap yang punya jendelanya sendiri; kuncinya `STAGE_TOOLS`. */
type Stage = 1 | 2 | 3 | 4 | 5 | 6;

/** Engine method slugs rendered as the names a petrophysicist would write. */
const METHOD_LABELS: Record<string, string> = {
  linear: "Linear (index)",
  larionov_tertiary: "Larionov (Tertiary)",
  larionov_older: "Larionov (older)",
  steiber: "Steiber",
  steiber_2: "Steiber 2",
  steiber_3: "Steiber 3",
  steiber_c: "Steiber (custom c)",
  clavier: "Clavier",
  density_neutron: "Density–neutron",
  density: "Density",
  neutron: "Neutron",
  sonic: "Sonic",
  // Porositas efektif. Slug-nya sendiri, bukan "density" yang dipakai tahap
  // dua: keduanya membaca RHOB, tetapi yang satu punya suku serpih di dalam
  // persamaannya dan yang lain tidak. Dua tahap yang mencetak "Densitas" untuk
  // dua hitungan yang berbeda adalah rel yang tidak bisa dibaca.
  total_shale: "Total − Vsh·PHIsh",
  density_shale: "Density − shale",
  neutron_shale: "Neutron − shale",
  density_neutron_shale: "Density–neutron − shale",
  sonic_shale: "Sonic − shale",
  archie: "Archie",
  indonesia: "Indonesia (Poupon–Leveaux)",
  simandoux: "Simandoux",
  waxman_smits: "Waxman–Smits",
  timur: "Timur",
  coates: "Coates",
  timur_coates: "Timur–Coates",
  morris_biggs: "Morris–Biggs",
};

const methodLabel = (method: string): string => METHOD_LABELS[method] ?? method;

/** Indikator serpih, dinamai sebagaimana kurvanya disebut di ruang kerja. */
const INDICATOR_LABELS: Record<string, string> = {
  gr: "GR",
  cgr: "CGR",
  thorium: "Th",
  potassium: "K",
  sp: "SP",
  resistivity: "RT",
  neutron: "NPHI",
  density: "RHOB",
  sonic: "DTC",
  neutron_density: "NPHI–RHOB",
  neutron_sonic: "NPHI–DTC",
  density_sonic: "RHOB–DTC",
};

/** Aturan penggabungan, sebagaimana mesin menuliskannya: "median(gr, sp)". */
const COMBINE_LABELS: Record<string, string> = {
  minimum: "min",
  maximum: "max",
  average: "mean",
  median: "median",
};

/**
 * Keterangan indikator sebuah tahap.
 *
 * Aturan gabungan datang dari mesin sebagai "median(gr, sp)": ia sudah
 * menyebut persis kurva mana yang ikut memilih, dan itulah yang perlu dibaca —
 * di sini tiap namanya saja yang ditukar dengan nama kurva di ruang kerja.
 * Yang tak dikenal dibiarkan apa adanya, sebab run lama boleh saja menyimpan
 * nama yang sudah tidak dipakai lagi.
 */
const indicatorLabel = (indicator: string): string => {
  const combined = /^(minimum|maximum|average|median)\((.*)\)$/.exec(indicator);
  if (!combined) return INDICATOR_LABELS[indicator] ?? indicator;
  const voters = combined[2]
    .split(",")
    .map((name) => INDICATOR_LABELS[name.trim()] ?? name.trim())
    .join(", ");
  return `${COMBINE_LABELS[combined[1]] ?? combined[1]}(${voters})`;
};

/**
 * Tahap yang medannya membuka sebuah jendela, dan judul tombolnya.
 *
 * Tahap yang tidak ada di sini mencetak metodenya di dalam kotak diam. Daftar
 * ini yang memutuskan, bukan sebuah rantai perbandingan nomor di dalam JSX:
 * tahap keempat dan seterusnya menyusul lewat satu baris di sini.
 */
const STAGE_TOOLS: Record<number, string> = {
  1: "Pick the shale volume method",
  2: "Pick the total porosity method",
  3: "Pick the effective porosity method",
  4: "Pick the water saturation method",
  5: "Pick the permeability method",
  // Tahap keenam tidak memilih metode: tidak ada persamaan yang bercabang di
  // sini, hanya tiga ambang. Judulnya menyebut apa yang diputuskannya, sebab
  // "Setel cutoff" saja tidak mengatakan cutoff atas apa.
  6: "Set the net pay cutoffs",
};

/** Tahap yang punya jendela, dalam urutan alurnya — dibaca, bukan diketik ulang. */
const TOOL_STAGES = Object.keys(STAGE_TOOLS).map(Number) as Stage[];

/**
 * Ketiga rel layar ini, masing-masing sebagai daftar tab yang sama bentuknya
 * dengan rel layar log — satu nama tiap rel, sebab tiap rel di sini adalah satu
 * kolom panel dan bukan empat tab. Namanya ditulis utuh, sebagaimana ia
 * tertulis di kepala relnya ketika terbuka.
 */
const WORKFLOW_TABS: RailTabItem<"workflow">[] = [
  { key: "workflow", label: "Deterministic workflow" },
];
const MULTIMIN_TABS: RailTabItem<"workflow">[] = [
  { key: "workflow", label: "Multi-mineral workflow" },
];
const QC_TABS: RailTabItem<"qc">[] = [{ key: "qc", label: "Results & QC" }];
const BATCH_TABS: RailTabItem<"wells">[] = [{ key: "wells", label: "Select wells" }];

/**
 * Apa yang dilaporkan sebuah tahap sesudah jendelanya menjalankan alurnya.
 *
 * Satu tabel, dengan alasan yang sama seperti `STAGE_TOOLS`: rantai
 * perbandingan nomor di dalam sebuah kalimat adalah tempat tahap keempat
 * mewarisi kata "porositas" dari tahap ketiga tanpa ada yang menyadarinya.
 *
 * Angka yang disebut tiap tahap adalah angka yang ia serahkan ke tahap
 * berikutnya, bukan kurva yang baru saja dihitungnya: tahap dua melaporkan
 * PHIE sebab PHIT bukan yang dibaca saturasi maupun cutoff, dan tahap empat
 * melaporkan SW sebab SW yang memutuskan net pay dan HCPV.
 */
const STAGE_REPORTS: Record<
  Stage,
  {
    noun: string;
    /**
     * Kata kerjanya, bila "dihitung" bukan yang dikerjakan tahap itu.
     *
     * Kelima tahap pertama memang menghitung sebuah kurva. Tahap keenam tidak
     * menghitung apa pun: ia menerapkan tiga ambang atas kurva yang sudah ada,
     * dan "cutoff dihitung" adalah kalimat yang menyebut pekerjaan yang tidak
     * dilakukannya.
     */
    verb?: string;
    reads: string;
    average: (total?: Partial<ZoneSummary>) => number | null | undefined;
    /** Tiga desimal cocok bagi sebuah pecahan, bukan bagi ribuan milidarcy. */
    format?: (value: number) => string;
  }
> = {
  1: { noun: "Vsh", reads: "mean", average: (total) => total?.vsh_avg },
  2: { noun: "Porosity", reads: "mean PHIE", average: (total) => total?.phie_avg },
  3: { noun: "Effective porosity", reads: "mean PHIE", average: (total) => total?.phie_avg },
  4: { noun: "Water saturation", reads: "mean SW", average: (total) => total?.sw_avg },
  5: {
    noun: "Permeability",
    reads: "mean k",
    average: (total) => total?.perm_avg,
    format: (value) => `${value.toFixed(value < 10 ? 2 : 1)} mD`,
  },
  // Yang diserahkan tahap enam bukan ke tahap berikutnya melainkan ke
  // volumetrik, dan yang diserahkannya adalah net pay. HCPV lebih ke hilir
  // lagi, tetapi net pay yang berpindah ketika ambangnya digeser satu langkah,
  // dan net pay pula yang dinilai penafsirnya masuk akal atau tidak.
  6: {
    noun: "Cutoff",
    verb: "applied",
    reads: "net pay",
    average: (total) => total?.net_pay,
    format: (value) => `${value.toFixed(1)} m`,
  },
};

/**
 * "Larionov (Tersier) · GR" — metode sebuah tahap beserta indikatornya.
 *
 * Dipisah karena tahap Vsh mencetaknya di dalam sebuah tombol dan tahap lain
 * di dalam sebuah kotak diam; yang dicetak keduanya sama persis, dan menyalin
 * dua baris itu adalah cara nama indikator berhenti muncul di salah satunya.
 */
function stepMethod(step: WorkflowStep) {
  // Kurva yang memerankan sebuah saluran, ketika ia bukan kurva yang senama.
  // Tanpa baris ini sebuah tahap melaporkan "Indonesia \u00b7 RT" atas sumur yang
  // sama sekali tidak membawa RT \u2014 benar menurut mesinnya, dan tidak
  // terverifikasi oleh siapa pun yang membaca daftar kurvanya.
  const swaps = Object.entries(step.sources ?? {});
  return (
    <>
      {methodLabel(step.method)}
      {step.indicator && (
        <span style={{ color: "var(--ink-6)", fontWeight: 400 }}>
          {` \u00b7 ${indicatorLabel(step.indicator)}`}
        </span>
      )}
      {swaps.length > 0 && (
        <span style={{ color: "var(--ink-6)", fontWeight: 400 }}>
          {` \u00b7 ${swaps.map(([channel, from]) => `${channel} from ${from}`).join(", ")}`}
        </span>
      )}
    </>
  );
}

/** Runs are typically milliseconds; "0.0 s" reads as though nothing happened. */
const formatDuration = (ms: number): string =>
  ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;

export default function Analysis({
  project,
  well,
  wells,
  onDataChanged,
}: {
  project: Project;
  well: Well;
  wells: Well[];
  onDataChanged: () => void;
}) {
  const toast = useToast();
  const mayCompute = canCompute(project.my_role);
  // Menyunting nilai kurva adalah menulis data, bukan menghitung: seorang
  // interpreter boleh membetulkan sebuah spike tanpa boleh menjalankan ulang
  // alurnya.
  const mayWrite = canWrite(project.my_role);
  // Lebar kedua rel layar ini. Rel alur kerja di kiri memuat daftar tahapan
  // beserta metodenya — nama seperti "Larionov (Tersier)" terpotong pada lebar
  // tetap — dan rel QC di kanan memuat tabel bandingan yang kolomnya menyempit
  // lebih dulu daripada apa pun.
  const [workflowWidth, setWorkflowWidth] = useStored("evolog.analysis.workflow", 322);
  const [qcWidth, setQcWidth] = useStored("evolog.analysis.qc", 330);
  // Kedua rel layar ini melipat dengan pola yang sama seperti rel layar log:
  // alurnya dibaca ketika sesuatu perlu disetel atau diperiksa, dan model
  // mineral di tengahnya — kolom komponen dan respons alat — adalah yang
  // paling menderita ketika 652 piksel di kiri dan kanannya tidak dipakai.
  // Keduanya disimpan, sebab rel yang terbuka lagi setiap kali layar dibuka
  // memaksa penggunanya melipatnya ulang setiap pagi.
  const [workflowCollapsed, setWorkflowCollapsed] = useStored(
    "evolog.analysis.workflow.collapsed", false);
  const [qcCollapsed, setQcCollapsed] = useStored("evolog.analysis.qc.collapsed", false);

  /**
   * Seluruh kurva sumur ini, beserta set asalnya.
   *
   * Diperlukan dua kali di layar ini: untuk menemukan kurva hasil tiap tahap —
   * VSH, PHIT, PHIE, SW, PERM — dan untuk menyusun daftar pembanding jendela
   * pemeriksanya. Dimuat ulang ketika sebuah hitungan selesai, sebab hitungan
   * itulah yang menerbitkan set kurva versi baru yang hendak dibuka orang.
   */
  const wellCurves = useAsync(
    (signal) =>
      well.curve_sets.length === 0
        ? Promise.resolve([])
        : api
            .get<Page<{
              id: number; mnemonic: string; unit: string; curve_set: number;
              color: string; is_active?: boolean;
            }>>(
              "/api/curves/",
              { curve_set: well.curve_sets.map((s) => s.id).join(",") },
              signal,
            )
            .then((page) => unpage(page, signal)),
    [well.id, well.curve_sets.map((s) => s.id).join(",")],
  );

  /**
   * Kurva yang benar-benar akan dilihat mesin, untuk pemilih sumber tiap tahap.
   *
   * Dari set **aktif** saja, sebab itu persis yang dikumpulkan `gather_logs`:
   * menawarkan kurva dari versi yang sudah dinonaktifkan adalah pilihan yang
   * bisa ditekan lalu memulangkan "tidak ada di sumur ini" dari run-nya sendiri.
   *
   * Satuannya ikut, dan bukan hiasan: ia yang memutuskan kurva mana yang
   * sejenis dengan sebuah saluran. Nama yang **dikenal** aplikasi ini sudah
   * dinormalkan saat impor — ILD, LLD dan RDEP menjadi RT sebelum menyentuh
   * basis data — jadi yang masih berdiri dengan nama sendiri justru yang tidak
   * dikenalnya, dan satuannya satu-satunya keterangan yang tersisa.
   *
   * Satu mnemonik boleh muncul di dua set aktif sekaligus; yang pertama yang
   * dipegang, dan diurutkan supaya daftarnya sama tiap kali jendelanya dibuka.
   */
  const availableCurves = useMemo(() => {
    const aktif = new Set(well.curve_sets.filter((s) => s.is_active).map((s) => s.id));
    const seen = new Map<string, { mnemonic: string; unit: string }>();
    for (const curve of wellCurves.data ?? []) {
      if (!aktif.has(curve.curve_set)) continue;
      // Log yang dimatikan tidak ditawarkan, dan penyaring ini harus ada di
      // sini meski `gather_logs` menyaringnya sendiri: daftar ini dibangun dari
      // `/api/curves/`, satu-satunya jalur yang sengaja memulangkan segalanya —
      // pohon proyek memerlukannya begitu, sebab di sanalah sakelarnya.
      // Tanpa baris ini sebuah RHOB yang dimatikan tetap bisa dipilih sebagai
      // sumber densitas, dan yang memulangkan jawabannya adalah run-nya:
      // "tidak ada di sumur ini", tentang kurva yang namanya baru saja dipilih
      // orangnya dari daftar ini.
      if (curve.is_active === false) continue;
      if (!seen.has(curve.mnemonic)) {
        seen.set(curve.mnemonic, { mnemonic: curve.mnemonic, unit: curve.unit });
      }
    }
    return [...seen.values()].sort((a, b) => a.mnemonic.localeCompare(b.mnemonic));
  }, [wellCurves.data, well.curve_sets]);

  /** Kurva yang sedang dibuka di jendela pemeriksa, bila ada. */
  const [inspect, setInspect] = useState<{
    id: number;
    mnemonic: string;
    color: string;
    versions: { id: number; label: string }[];
  } | null>(null);

  /**
   * Kurva hasil sebuah tahap, dicari di set yang **aktif**.
   *
   * Set aktif dan bukan set terbaru: yang digambar lembar log dan yang dipakai
   * hitungan berikutnya adalah set aktif, jadi itu pula yang harus dibuka
   * ketika seseorang menekan hasil sebuah tahap. Membuka set terbaru akan
   * memperlihatkan angka yang berbeda dari yang tergambar di lembar, tanpa
   * satu pun keterangan yang menjelaskan selisihnya.
   */
  const stageCurve = (stage: number) => {
    const mnemonic = STAGE_CURVES[stage];
    if (!mnemonic) return null;
    const aktif = new Set(well.curve_sets.filter((s) => s.is_active).map((s) => s.id));
    const semua = wellCurves.data ?? [];
    return (
      semua.find((c) => c.mnemonic === mnemonic && aktif.has(c.curve_set)) ??
      semua.find((c) => c.mnemonic === mnemonic) ??
      null
    );
  };

  const bukaKurva = (stage: number) => {
    const curve = stageCurve(stage);
    if (!curve) return;
    setInspect({
      id: curve.id,
      mnemonic: curve.mnemonic,
      color: defaultCurveColor(curve.mnemonic, curve.color),
      versions: kinOptions(
        curve.mnemonic,
        curve.id,
        (wellCurves.data ?? []).map((other) => {
          const from = well.curve_sets.find((set) => set.id === other.curve_set);
          return {
            id: other.id,
            mnemonic: other.mnemonic,
            setLabel: from
              ? `${from.name} v${from.version}${from.is_active ? "" : " · older"}`
              : `set ${other.curve_set}`,
            active: from?.is_active,
            off: other.is_active === false,
          };
        }),
      ),
    });
  };
  const [mode, setMode] = useState<Mode>("deterministic");
  /**
   * Pekerjaan mana yang sedang berjalan — bukan sekadar *ada* yang berjalan.
   *
   * Keempat tombolnya memanggil endpoint yang berbeda dan dua di antaranya
   * tampil bersamaan di layar, jadi sebuah boolean tidak bisa mengatakan yang
   * mana yang harus berputar. Sebelum ini yang menjawabnya adalah `mode`, yaitu
   * tab yang sedang disorot — dan tab bukan tombol yang ditekan: menekan "Run
   * the workflow" selagi tab Multi-mineral tersorot memutar "Solve the
   * interval", sekaligus mematikannya dan membiarkan tombol yang barusan
   * ditekan tetap hidup untuk ditekan lagi.
   */
  const [running, setRunning] = useState<Job | null>(null);
  // Tahap mana yang jendelanya terbuka. Semuanya menumpang parameter set yang
  // sama dengan tombol "Jalankan alur", jadi yang dipilih di sini adalah yang
  // dipakai run berikutnya — bukan setelan kedua yang hidup sendiri.
  const [tool, setTool] = useState<Stage | null>(null);
  // Jendela "Uji satu titik". Ia tidak menulis apa pun, jadi ia tidak
  // menumpang `running` bersama keempat pekerjaan yang menulis.
  const [pointTest, setPointTest] = useState(false);
  // Tahap pertama alur multi-mineral: daftar mineralnya. Terpisah dari `tool`,
  // yang menomori tahap deterministik — dua alur yang berbagi satu keadaan
  // "tahap mana yang terbuka" adalah dua jendela yang bisa terbuka bersamaan
  // atas nomor yang sama.
  const [mineralTool, setMineralTool] = useState(false);
  const [batchSelection, setBatchSelection] = useState<Set<number>>(new Set());
  const [batchResults, setBatchResults] = useState<
    { well: string; status: string; net_pay?: number | null; detail?: string }[] | null
  >(null);

  const parameterSets = useAsync(
    (signal) =>
      api
        .get<Page<ParameterSet>>("/api/parameter-sets/", { project: project.id, latest: 1 }, signal)
        .then((page) => unpage(page, signal)),
    [project.id],
  );
  const models = useAsync(
    (signal) =>
      api
        .get<Page<MineralModel>>(
          "/api/mineral-models/",
          // Satu baris per nama. Modelnya berversi sejak ia menepati janji
          // reproduksinya, dan tanpa saringan ini menu model akan menawarkan
          // tiap versi lama sebagai model yang berdiri sendiri.
          { project: project.id, latest: 1 },
          signal,
        )
        .then((page) => unpage(page, signal)),
    [project.id],
  );
  const detRuns = useAsync(
    (signal) =>
      api
        .get<Page<AnalysisRun>>("/api/runs/", { well: well.id, kind: "deterministic" }, signal)
        .then((page) => unpage(page, signal)),
    [well.id],
  );
  const mmRuns = useAsync(
    (signal) =>
      api.get<Page<AnalysisRun>>("/api/runs/", { well: well.id, kind: "multimin" }, signal).then((page) => unpage(page, signal)),
    [well.id],
  );

  const [modelId, setModelId] = useState<number | null>(null);
  const model = models.data?.find((m) => m.id === modelId) ?? models.data?.[0] ?? null;
  const parameterSet = parameterSets.data?.[0] ?? null;

  const latestDet = detRuns.data?.[0] ?? null;
  const latestMmId = mmRuns.data?.[0]?.id ?? null;
  // The list endpoint omits the QC payload; fetch the detail for the charts.
  const mmDetail = useAsync(
    (signal) => api.get<AnalysisRun>(`/api/runs/${latestMmId}/`, undefined, signal),
    [latestMmId],
    { skip: latestMmId === null },
  );

  /**
   * Keempat tahap alur multi-mineral, dibaca dari model hidup dan solve terakhirnya.
   *
   * Dihitung di sini dan bukan di dalam relnya supaya kepala rel bisa menyebut
   * cacah tahapnya tanpa menghitungnya sendiri — dua tempat yang masing-masing
   * menghitung jumlah yang sama adalah dua tempat yang boleh berselisih.
   */
  const mmStages = multiminStages(model, mmDetail.data ?? null);

  const zone = well.zones[0] ?? null;

  /**
   * Kedalaman yang dibuka jendela "Uji satu titik", dan selang tempatnya duduk.
   *
   * Zona lebih dulu, sebab selang itulah yang dikirim tombol "Selesaikan
   * interval" — sebuah titik yang diuji di luar selang yang dipecahkan menguji
   * penyelesaian yang lain. Sumur tanpa zona memakai selang solve terakhirnya;
   * yang belum punya keduanya belum punya angka apa pun untuk ditawarkan, dan
   * tombolnya mengatakan itu alih-alih menawarkan tebakan.
   */
  const probeSpan: [number, number] | null = zone
    ? [zone.top_md, zone.base_md]
    : (mmDetail.data?.summary?.interval ?? null);
  const probeDepth = probeSpan ? (probeSpan[0] + probeSpan[1]) / 2 : null;

  const runDeterministic = async () => {
    setRunning("workflow");
    try {
      const run = await api.post<AnalysisRun>("/api/compute/deterministic/", {
        well: well.id,
        parameter_set: parameterSet?.id,
      });
      toast(`Workflow done — net pay ${run.summary.total?.net_pay ?? "—"} m.`);
      detRuns.reload();
      onDataChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setRunning(null);
    }
  };

  /**
   * Alur yang dijalankan dari jendela sebuah tahap.
   *
   * Yang berpindah hanyalah pilihan satu tahap, tetapi yang dihitung tetap
   * enam: porositas efektif membaca Vsh dan PHIT, saturasi membaca porositas
   * efektif itu, dan cutoff memutuskan atas keduanya. Sebuah kartu yang
   * menyebut metode baru di sebelah net pay lama adalah layar yang berbohong.
   * Override-nya menerbitkan versi parameter set baru di server, jadi hasilnya
   * tetap bisa ditelusuri kembali.
   */
  const runStage = async (stage: Stage, overrides: Partial<ParameterValues>) => {
    setRunning("stage");
    try {
      const run = await api.post<AnalysisRun>("/api/compute/deterministic/", {
        well: well.id,
        parameter_set: parameterSet?.id,
        overrides: Object.keys(overrides).length ? overrides : undefined,
      });
      const step = run.steps.find((s) => s.n === stage);
      const report = STAGE_REPORTS[stage];
      const average = report.average(run.summary.total);
      const rata =
        average === null || average === undefined
          ? "—"
          : (report.format ?? ((value: number) => value.toFixed(3)))(average);
      const indicator = step?.indicator ? ` over ${indicatorLabel(step.indicator)}` : "";
      toast(
        `${report.noun} ${report.verb ?? "computed"} — ${methodLabel(step?.method ?? "")}` +
          `${indicator} · ${report.reads} ${rata}.`,
      );
      setTool(null);
      detRuns.reload();
      parameterSets.reload();
      onDataChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setRunning(null);
    }
  };

  const runMultimineral = async () => {
    setRunning("multimin");
    try {
      const run = await api.post<AnalysisRun>("/api/compute/multimineral/", {
        well: well.id,
        mineral_model: model?.id,
        top: zone?.top_md,
        base: zone?.base_md,
      });
      const solver = run.qc?.solver;
      toast(
        `Solve done — incoherence ${solver?.mean_incoherence ?? "—"}, ` +
          `${solver?.failed_points ?? 0} points failed to converge.`,
      );
      mmRuns.reload();
      onDataChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setRunning(null);
    }
  };

  /**
   * Menyimpan daftar mineral — yaitu menerbitkan versi baru modelnya.
   *
   * `PUT` dan bukan `PATCH` in-place, sebab server memang menjawabnya dengan
   * baris baru: tiap solve yang sudah berjalan tetap menunjuk versi yang
   * dipakainya, jadi hasil lama tetap bisa mengatakan ia dihitung dari angka
   * yang mana. Yang dipilih di layar berpindah ke versi baru itu, sebab yang
   * baru saja disunting orang adalah yang ingin dilihatnya.
   */
  const saveComponents = async (components: MineralComponent[]) => {
    if (!model) return;
    setRunning("mineral");
    try {
      const published = await api.put<MineralModel>(`/api/mineral-models/${model.id}/`, {
        components,
      });
      await models.reload();
      setModelId(published.id);
      setMineralTool(false);
      toast(`${published.name} v${published.version} published — ${components.length} components.`);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setRunning(null);
    }
  };

  const runBatch = async () => {
    setRunning("batch");
    setBatchResults(null);
    try {
      const result = await api.post<{ results: typeof batchResults }>("/api/compute/batch/", {
        project: project.id,
        wells: [...batchSelection],
        parameter_set: parameterSet?.id,
      });
      setBatchResults(result.results);
      const done = (result.results ?? []).filter((row) => row.status === "done").length;
      toast(`Batch finished — ${done} of ${batchSelection.size} wells succeeded.`);
      onDataChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setRunning(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          height: 38,
          flex: "none",
          padding: "0 12px",
          background: "var(--toolbar)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <span style={{ font: "600 11.5px var(--font-sans)", color: "var(--ink)" }}>
          Analysis module — {well.name}
        </span>
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
          {zone ? `interval ${zone.top_md.toFixed(0)}–${zone.base_md.toFixed(0)} m · ${zone.name}` : "all wells"}
        </span>
        <span style={{ flex: 1 }} />
        <Segmented
          value={mode}
          options={[
            { value: "deterministic", label: "Deterministic" },
            { value: "multimin", label: "Multi-mineral" },
            { value: "batch", label: "Batch" },
            { value: "lumping", label: "Lumping" },
            { value: "rocktype", label: "Rock typing" },
          ]}
          onChange={setMode}
        />
      </div>

      {mode === "rocktype" ? (
        <RockTyping project={project} wells={wells} />
      ) : mode === "lumping" ? (
        <LumpingPanel projectId={project.id} />
      ) : mode === "batch" ? (
        <BatchPanel
          projectId={project.id}
          wells={wells}
          selection={batchSelection}
          onToggle={(id) => {
            const next = new Set(batchSelection);
            next.has(id) ? next.delete(id) : next.add(id);
            setBatchSelection(next);
          }}
          onSelect={(ids) => setBatchSelection(new Set(ids))}
          onRun={runBatch}
          running={running === "batch"}
          mayCompute={mayCompute}
          results={batchResults}
          parameterSet={parameterSet}
        />
      ) : (
        <div style={{ display: "flex", alignItems: "stretch", flex: 1, minHeight: 0 }}>
          {/* Workflow rail */}
          {workflowCollapsed ? (
            <RailStrip
              side="left"
              tabs={mode === "multimin" ? MULTIMIN_TABS : WORKFLOW_TABS}
              active="workflow"
              onSelect={() => setWorkflowCollapsed(false)}
              expandLabel="Show the workflow panel"
            />
          ) : (
            <aside
              style={{
                width: workflowWidth,
                flex: "none",
                background: "var(--rail)",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  // Menyusut dari 10 piksel: tombol lipat setinggi 22 piksel
                  // duduk di baris ini, dan kepala rel yang tumbuh karenanya
                  // tidak lagi sejajar dengan kepala kolom di sebelahnya.
                  padding: "5px 7px 5px 13px",
                  borderBottom: "1px solid var(--line-soft)",
                  flex: "none",
                }}
              >
                <span className="label">
                  {mode === "multimin" ? "Multi-mineral workflow" : "Deterministic workflow"}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                    {mode === "multimin"
                      ? `${mmStages.length} stages`
                      : `${latestDet?.steps.length ?? 6} stages`}
                  </span>
                  <RailCollapseButton
                    side="left"
                    collapsed={false}
                    label="Hide the workflow panel"
                    onClick={() => setWorkflowCollapsed(true)}
                  />
                </div>
              </div>

              <div
                className="scroll-y"
                style={{ flex: 1, padding: "11px 13px", display: "flex", flexDirection: "column", gap: 9 }}
              >
                {mode === "multimin" && (
                  <>
                    {models.loading && <Loading />}
                    {!models.loading && !model && (
                      <Notice>
                        This project has no multi-mineral model yet. Run the solve to create a
                        default one.
                      </Notice>
                    )}
                    <MultiminStages
                      stages={mmStages}
                      mayCompute={mayCompute}
                      onOpen={(stage) => stage === 1 && model && setMineralTool(true)}
                    />
                  </>
                )}
                {mode !== "multimin" && detRuns.loading && <Loading />}
                {mode !== "multimin" && !detRuns.loading && !latestDet && (
                  <>
                    <Notice>No computation yet. Run the workflow to see the status of each stage.</Notice>
                    {/* Sebelum run pertama tidak ada kartu tahap yang bisa
                        diklik, dan metodenya justru paling ingin dipilih sebelum
                        hitungan pertama — bukan sesudahnya. */}
                    {parameterSet &&
                      TOOL_STAGES.map((stage) => (
                        <Button
                          key={stage}
                          variant="ghost"
                          disabled={!mayCompute}
                          onClick={() => setTool(stage)}
                        >
                          {STAGE_TOOLS[stage]}
                        </Button>
                      ))}
                  </>
                )}
                {mode !== "multimin" && latestDet?.steps.map((step) => (
                  <div
                    key={step.n}
                    style={{
                      display: "flex",
                      gap: 10,
                      padding: "10px 11px",
                      background: "var(--panel)",
                      border: "1px solid var(--border-control)",
                      borderRadius: "var(--radius-panel)",
                      boxShadow: "var(--shadow-panel)",
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 19,
                        height: 19,
                        flex: "none",
                        borderRadius: "50%",
                        background: "var(--control)",
                        border: "1px solid var(--border-hover)",
                        font: "600 9.5px var(--font-mono)",
                        color: "var(--ink-2)",
                      }}
                    >
                      {step.n}
                    </span>
                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 5 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ font: "600 11px var(--font-sans)", color: "var(--ink)" }}>{step.name}</span>
                        <span style={{ flex: 1 }} />
                        <StatusDot tone={step.ok ? "ok" : "warn"} />
                      </div>
                      {/* Tahap yang medannya bisa diklik: pilihan metodenya ada
                          di jendela yang terbuka dari sini, dan `STAGE_TOOLS`
                          yang memutuskan tahap mana. Tahap selebihnya masih
                          melaporkan metodenya saja, sebab parameternya disunting
                          di rel ruang kerja bersama kurvanya. */}
                      {STAGE_TOOLS[step.n] && parameterSet ? (
                        <MethodButton
                          title={STAGE_TOOLS[step.n]}
                          disabled={!mayCompute}
                          onClick={() => setTool(step.n as Stage)}
                        >
                          {stepMethod(step)}
                        </MethodButton>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: 25,
                            padding: "0 8px",
                            background: "var(--control)",
                            border: "1px solid var(--border-control)",
                            borderRadius: "var(--radius)",
                            font: "500 10.5px var(--font-sans)",
                            color: "var(--ink)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {stepMethod(step)}
                        </div>
                      )}
                      <span style={{ font: "400 9.5px/1.4 var(--font-mono)", color: "var(--ink-6)" }}>
                        {step.params}
                      </span>
                      {/* Kurva hasil tahap ini, dibuka di jendela yang sama
                          dengan yang dibuka pohon proyek layar Log: grafik dan
                          tabel, keduanya bisa disunting, dengan pembanding
                          sejenis di belakangnya.

                          Barisnya hanya muncul ketika kurvanya memang ada.
                          Sebuah tombol yang menjanjikan kurva lalu membuka
                          jendela kosong lebih buruk daripada tidak ada tombol:
                          yang menekannya akan mengira hitungannya gagal,
                          padahal ia memang belum dijalankan. */}
                      {stageCurve(step.n) && (
                        <button
                          type="button"
                          onClick={() => bukaKurva(step.n)}
                          title={`Open ${STAGE_CURVES[step.n]} — its graph, table and editing`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            alignSelf: "flex-start",
                            padding: "2px 7px 2px 5px",
                            background: "transparent",
                            border: "1px solid var(--border-control)",
                            borderRadius: "var(--radius)",
                            cursor: "pointer",
                            font: "500 9.5px var(--font-mono)",
                            color: "var(--accent)",
                          }}
                        >
                          <span
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: 2,
                              flex: "none",
                              background: defaultCurveColor(
                                STAGE_CURVES[step.n],
                                stageCurve(step.n)?.color,
                              ),
                            }}
                          />
                          {STAGE_CURVES[step.n]}
                          <span style={{ color: "var(--ink-6)" }}>open &amp; edit</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {mode !== "multimin" &&
                  (latestDet?.warnings ?? []).map((warning, index) => (
                    <Notice key={index}>{warning}</Notice>
                  ))}
              </div>

              <div
                style={{
                  padding: "11px 13px",
                  borderTop: "1px solid var(--line-soft)",
                  display: "flex",
                  gap: 8,
                  flex: "none",
                }}
              >
                {mode === "multimin" ? (
                  <Button
                    variant="primary"
                    style={{ flex: 1 }}
                    busy={running === "multimin"}
                    disabled={!mayCompute}
                    onClick={runMultimineral}
                  >
                    Solve the interval
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      style={{ flex: 1 }}
                      busy={running === "workflow"}
                      disabled={!mayCompute}
                      onClick={runDeterministic}
                    >
                      Run the workflow
                    </Button>
                    <Button variant="ghost" disabled={!mayCompute}>
                      Add a stage
                    </Button>
                  </>
                )}
              </div>
            </aside>
          )}
          {!workflowCollapsed && (
            <Resizer
              width={workflowWidth}
              onChange={setWorkflowWidth}
              min={250}
              max={520}
              side="left"
              label="Resize the workflow panel"
            />
          )}

          {/* Mineral model */}
          <section
            className="scroll-y"
            style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", background: "var(--canvas)" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                borderBottom: "1px solid var(--line-soft)",
                flex: "none",
              }}
            >
              <span style={{ font: "600 11px var(--font-sans)", color: "var(--ink)" }}>
                {mode === "multimin" ? "Multi-mineral model" : "Zone summary"}
              </span>
              <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                {mode !== "multimin"
                  ? latestDet
                    ? `${(latestDet.summary.zones ?? []).length} zones · ${
                        latestDet.summary.total?.net_pay?.toFixed(1) ?? "—"} m net pay`
                    : "no run yet"
                  : model
                    ? `${model.components.length} components · ${model.tools.filter((t) => t.enabled).length} tool responses`
                    : "—"}
              </span>
              <span style={{ flex: 1 }} />
              {mode === "multimin" && models.data && models.data.length > 0 && (
                <Dropdown
                  value={model?.id ?? 0}
                  // Versinya, bukan preset-nya: preset sebuah model tidak pernah
                  // berubah, jadi ia tidak mengatakan apa pun tentang model mana
                  // yang sedang dibaca. Sama dengan menu set parameter.
                  options={models.data.map((m) => ({
                    value: m.id,
                    label: m.name,
                    hint: `v${m.version}`,
                  }))}
                  onChange={setModelId}
                  width={220}
                  compact
                />
              )}
            </div>

            {mode !== "multimin" && <DeterministicResults run={latestDet} />}

            {mode === "multimin" && models.loading && <Loading />}
            {mode === "multimin" && !models.loading && !model && (
              <Notice>This project has no multi-mineral model yet. Run the solve to create a default one.</Notice>
            )}

            {mode === "multimin" && model && (
              <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
                <div
                  style={{
                    border: "1px solid var(--border-control)",
                    borderRadius: "var(--radius-panel)",
                    overflow: "hidden",
                    boxShadow: "var(--shadow-panel)",
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "var(--panel-header)" }}>
                        {["Component", "RHOB", "NPHI", "DTC", "GR", "Bounds", "Mean V", "± σ"].map((head, index) => (
                          <th
                            key={head}
                            style={{
                              padding: "7px 9px",
                              textAlign: index === 0 ? "left" : "right",
                              font: "600 9.5px var(--font-mono)",
                              color: "var(--ink-2)",
                              letterSpacing: ".06em",
                              borderBottom: "1px solid var(--line-strong)",
                            }}
                          >
                            {head}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {model.components.map((component) => {
                        const entry = mmDetail.data?.summary.components?.find(
                          (row) => row.name === component.name,
                        );
                        const mean = entry?.mean_volume;
                        const sigma = entry?.mean_sigma;
                        return (
                          <tr key={component.name} style={{ background: "var(--panel)" }}>
                            <td style={{ padding: "7px 9px", borderBottom: "1px solid var(--line)" }}>
                              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span
                                  style={{
                                    width: 11,
                                    height: 11,
                                    borderRadius: 3,
                                    background: component.color,
                                    border: "1px solid rgba(0,0,0,.4)",
                                    flex: "none",
                                  }}
                                />
                                <span style={{ font: "500 11px var(--font-sans)", color: "var(--ink)" }}>
                                  {component.name}
                                </span>
                              </span>
                            </td>
                            {(["RHOB", "NPHI", "DTC", "GR"] as const).map((tool) => (
                              <td key={tool} style={numberCell()}>
                                {Number(component[tool]).toFixed(tool === "GR" ? 0 : 3)}
                              </td>
                            ))}
                            {/* Batas per komponen. Sebuah mineral yang dipaku
                                di nol karena diketahui tidak ada di sumur ini
                                tetap berdiri di tabel dan di legendanya —
                                mengeluarkannya dari model akan menghilangkannya
                                dari keduanya. */}
                            <td style={{ ...numberCell(), color: "var(--ink-5)" }}>
                              {describeBounds(component)}
                            </td>
                            <td style={{ ...numberCell(), color: "var(--accent)", fontWeight: 600 }}>
                              {mean !== null && mean !== undefined ? mean.toFixed(2) : "—"}
                            </td>
                            {/* "Kuarsa 40 %" dan "kuarsa 40 % ± 25" keluar dari
                                penyelesai yang sama, dan hanya yang kedua boleh
                                dibaca sebagai tebakan. */}
                            <td
                              style={{
                                ...numberCell(),
                                color: sigma !== null && sigma !== undefined && sigma >= 0.5
                                  ? "var(--amber)"
                                  : "var(--ink-4)",
                              }}
                            >
                              {sigma !== null && sigma !== undefined ? sigma.toFixed(2) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 9px",
                      background: "var(--rail)",
                    }}
                  >
                    <Button
                      variant="ghost"
                      style={{ height: 24 }}
                      disabled={!mayCompute}
                      onClick={() => setMineralTool(true)}
                    >
                      {Icon.plus({ size: 10 })} Add a component
                    </Button>
                    <Button variant="ghost" style={{ height: 24 }} disabled={!mayCompute}>
                      Import endpoints from core
                    </Button>
                    <span style={{ flex: 1 }} />
                    <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                      volumes sum to 1.000
                    </span>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1.15fr .95fr .95fr", gap: 12 }}>
                  <Panel title="Tool responses, weights & equations">
                    {model.tools.map((tool) => {
                      const equation = tool.equation ?? DEFAULT_EQUATION[tool.name] ?? "linear";
                      return (
                        <div key={tool.name} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                          <Checkbox checked={tool.enabled} onChange={() => undefined} label={tool.name} />
                          {/* Persamaan mana yang meramalkan bacaan alat ini.
                              "PEF 1,89 terukur lawan 1,89 termodelkan" berarti
                              dua hal yang berbeda menurut apakah yang
                              memodelkannya campuran volumetrik atau penampang
                              lintang, dan yang membacanya berhak tahu yang mana. */}
                          <span
                            style={{
                              font: "500 9px var(--font-mono)",
                              color: equation === "linear" ? "var(--ink-6)" : "var(--accent)",
                              border: "1px solid var(--line)",
                              borderRadius: 3,
                              padding: "1px 4px",
                              flex: "none",
                            }}
                            title={EQUATION_HINT[equation]}
                          >
                            {EQUATION_LABEL[equation] ?? equation}
                          </span>
                          <span style={{ flex: 1 }} />
                          <span
                            style={{
                              font: "500 10.5px var(--font-mono)",
                              color: "var(--ink-4)",
                              whiteSpace: "nowrap",
                              flex: "none",
                            }}
                          >
                            {tool.enabled ? `w ${tool.weight.toFixed(1)} · σ ${tool.uncertainty}` : "—"}
                          </span>
                        </div>
                      );
                    })}
                  </Panel>

                  <Panel title="Constraints">
                    <KeyValue k="Volume sum" v="= 1.000" />
                    <KeyValue k="Mineral volumes" v="≥ 0" />
                    <KeyValue k="Max clay" v={`≤ ${String(model.constraints.max_clay ?? "—")}`} />
                    <KeyValue k="Tolerance" v={String(model.constraints.tolerance ?? "1e-4")} />
                    {/* Komponen yang punya persamaan a-priori sendiri. Satu
                        skalar redaman tidak bisa mengatakan bahwa air terikat
                        dipatok ketat sementara kuarsa mengambang bebas. */}
                    <KeyValue
                      k="Priors with sigma"
                      v={`${model.components.filter((c) => c.prior_sigma).length} / ${model.components.length}`}
                    />
                    <KeyValue
                      k="Endpoints per zone"
                      v={
                        mmDetail.data?.qc?.solver?.zones?.length
                          ? mmDetail.data.qc.solver.zones.join(", ")
                          : "—"
                      }
                    />
                  </Panel>

                  <Panel title="Solver">
                    {mmDetail.data?.qc?.solver ? (
                      <>
                        <KeyValue
                          k="Method"
                          v={mmDetail.data.qc?.solver!.nonlinear
                            ? "Levenberg–Marquardt"
                            : "Least squares"}
                        />
                        <KeyValue k="Mean iterations" v={mmDetail.data.qc?.solver!.mean_iterations ?? "—"} />
                        {/* Dalam sigma: 1,0 berarti tiap alat meleset rata-rata
                            satu simpangan bakunya sendiri. Tanpa satuannya
                            angka ini tidak bisa dibandingkan dengan apa pun. */}
                        <KeyValue
                          k="Mean incoherence"
                          v={mmDetail.data.qc?.solver!.mean_incoherence === null
                            || mmDetail.data.qc?.solver!.mean_incoherence === undefined
                            ? "—"
                            : `${mmDetail.data.qc!.solver!.mean_incoherence!.toFixed(2)} σ`}
                        />
                        <KeyValue
                          k="Points that failed to converge"
                          v={`${mmDetail.data.qc?.solver!.failed_points} / ${mmDetail.data.qc?.solver!.samples}`}
                        />
                        <KeyValue k="Degrees of freedom" v={mmDetail.data.qc?.solver!.degrees_of_freedom} />
                        <KeyValue k="Solve time" v={formatDuration(mmDetail.data.duration_ms)} />
                      </>
                    ) : (
                      <span style={{ font: "400 10.5px var(--font-sans)", color: "var(--ink-5)" }}>
                        No solve yet.
                      </span>
                    )}
                  </Panel>
                </div>

                {/* "Solve the interval" dulu berdiri di sini. Ia pindah ke kaki
                    rel alurnya, tempat "Run the workflow" berdiri di tab
                    sebelah: ia tindakan milik alurnya, bukan milik tabel
                    modelnya, dan dua tombol bertuliskan sama di satu layar
                    adalah satu tombol lebih banyak daripada yang ada. */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 2, flexWrap: "wrap" }}>
                  {/* Tidak bergantung pada `mayCompute`: ia tidak menulis
                      run, kurva maupun baris audit, dan peran hanya-baca
                      justru pemeriksa tafsiran orang lain. */}
                  <Button
                    disabled={probeDepth === null}
                    title={
                      probeDepth === null
                        ? "This well has neither a zone nor a solve, so its depth interval is not known yet."
                        : undefined
                    }
                    onClick={() => setPointTest(true)}
                  >
                    Test a single point
                  </Button>
                  <Button variant="ghost" disabled={!mayCompute}>
                    Compare with deterministic
                  </Button>
                  <span style={{ flex: 1 }} />
                  {mmDetail.data?.qc?.solver?.regularised && (
                    <Notice>
                      Underdetermined system ({mmDetail.data.qc?.solver!.degrees_of_freedom} degrees of freedom) —
                      solved with regularisation (damping {mmDetail.data.qc?.solver!.damping}).
                    </Notice>
                  )}
                  {model.tools.every((tool) => tool.name !== "PEF" || !tool.enabled) && (
                    <Notice>PEF disabled: dolomite cannot be separated.</Notice>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* QC rail */}
          {!qcCollapsed && (
            <Resizer
              width={qcWidth}
              onChange={setQcWidth}
              min={260}
              max={560}
              label="Resize the QC panel"
            />
          )}
          {qcCollapsed ? (
            <RailStrip
              tabs={QC_TABS}
              active="qc"
              onSelect={() => setQcCollapsed(false)}
              expandLabel="Show the QC panel"
            />
          ) : (
            <aside
              style={{
                width: qcWidth,
                flex: "none",
                background: "var(--rail)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "5px 7px 5px 13px",
                  borderBottom: "1px solid var(--line-soft)",
                  flex: "none",
                }}
              >
                <span className="label">Results &amp; QC</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {mode === "multimin" && mmDetail.data?.qc?.solver && (
                    <span
                      style={{
                        font: "400 9.5px var(--font-mono)",
                        color: mmDetail.data.qc?.solver!.converged ? "var(--accent)" : "var(--amber)",
                      }}
                    >
                      ● {mmDetail.data.qc?.solver!.converged ? "converged" : "partly failed"}
                    </span>
                  )}
                  <RailCollapseButton
                    side="right"
                    collapsed={false}
                    label="Hide the QC panel"
                    onClick={() => setQcCollapsed(true)}
                  />
                </div>
              </div>

              <div
                className="scroll-y"
                style={{ flex: 1, padding: "11px 13px", display: "flex", flexDirection: "column", gap: 11 }}
              >
                {mode !== "multimin" && <DeterministicQc run={latestDet} />}
                {mode === "multimin" && mmDetail.loading && <Loading />}
                {mode === "multimin" && !latestMmId && (
                  <Empty>Run the multi-mineral solve to see the QC.</Empty>
                )}
                {mode === "multimin" && mmDetail.data && model && (
                  <>
                    <VolumetricTrack run={mmDetail.data} model={model} />
                    <ReconstructionChart run={mmDetail.data} />
                    <ResidualPanel run={mmDetail.data} />
                    <Panel title="Volumetric legend">
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5px 12px" }}>
                        {model.components.map((component) => (
                          <div key={component.name} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <span
                              style={{
                                width: 11,
                                height: 9,
                                background: component.color,
                                border: "1px solid rgba(0,0,0,.4)",
                                flex: "none",
                              }}
                            />
                            <span
                              style={{
                                font: "400 10px var(--font-sans)",
                                color: "var(--ink-3)",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {component.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </Panel>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button variant="primary" style={{ flex: 1 }} disabled={!mayCompute}>
                        Save to a set
                      </Button>
                      <Button variant="ghost" disabled={!mayCompute}>
                        Laporan
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </aside>
          )}
          {tool === 1 && parameterSet && (
            <VshDialog
              params={parameterSet.resolved}
              available={availableCurves}
              busy={running === "stage"}
              disabled={!mayCompute}
              onClose={() => setTool(null)}
              onRun={(overrides) => runStage(1, overrides)}
            />
          )}
          {tool === 2 && parameterSet && (
            <PhitDialog
              params={parameterSet.resolved}
              available={availableCurves}
              busy={running === "stage"}
              disabled={!mayCompute}
              onClose={() => setTool(null)}
              onRun={(overrides) => runStage(2, overrides)}
            />
          )}
          {tool === 3 && parameterSet && (
            <PhieDialog
              params={parameterSet.resolved}
              available={availableCurves}
              busy={running === "stage"}
              disabled={!mayCompute}
              onClose={() => setTool(null)}
              onRun={(overrides) => runStage(3, overrides)}
            />
          )}
          {tool === 4 && parameterSet && (
            <SwDialog
              params={parameterSet.resolved}
              available={availableCurves}
              busy={running === "stage"}
              disabled={!mayCompute}
              onClose={() => setTool(null)}
              onRun={(overrides) => runStage(4, overrides)}
            />
          )}
          {tool === 5 && parameterSet && (
            <PermDialog
              params={parameterSet.resolved}
              busy={running === "stage"}
              disabled={!mayCompute}
              onClose={() => setTool(null)}
              onRun={(overrides) => runStage(5, overrides)}
            />
          )}
          {tool === 6 && parameterSet && (
            <CutoffDialog
              params={parameterSet.resolved}
              busy={running === "stage"}
              disabled={!mayCompute}
              onClose={() => setTool(null)}
              onRun={(overrides) => runStage(6, overrides)}
            />
          )}
          {mineralTool && model && (
            <MineralComponentsDialog
              model={model}
              busy={running === "mineral"}
              disabled={!mayCompute}
              onClose={() => setMineralTool(false)}
              onSave={saveComponents}
            />
          )}
          {/* Jendela pemeriksa kurva — komponen yang sama dengan yang dibuka
              pohon proyek layar Log. Menyimpan suntingan menerbitkan set kurva
              versi baru, jadi daftar kurva layar ini dan lembar log di layar
              sebelah harus dimuat ulang; `onDataChanged` yang mengurusnya. */}
          {inspect && (
            <CurveInspector
              curveId={inspect.id}
              mnemonic={inspect.mnemonic}
              color={inspect.color}
              versions={inspect.versions}
              mayEdit={mayWrite}
              onClose={() => setInspect(null)}
              onSaved={({ version, points }) => {
                toast(`${inspect.mnemonic}: ${points} points saved as version ${version}.`);
                setInspect(null);
                wellCurves.reload();
                onDataChanged();
              }}
            />
          )}
          {pointTest && model && probeDepth !== null && (
            <PointTestDialog
              well={well}
              model={model}
              top={probeSpan?.[0]}
              base={probeSpan?.[1]}
              defaultDepth={probeDepth}
              onClose={() => setPointTest(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function numberCell(): React.CSSProperties {
  return {
    padding: "7px 9px",
    textAlign: "right",
    font: "400 11px var(--font-mono)",
    color: "var(--ink-2)",
    borderBottom: "1px solid var(--line)",
  };
}

/** Tinggi lembar volumetrik di dalam rel QC, sebelum ia diperbesar sendiri. */
const VOLUMETRIC_PANE_H = 330;

/** Mistar setipis ini masih memuat angka empat digit di rel selebar 260 px. */
const VOLUMETRIC_GUTTER_W = 48;

/**
 * Pita volume bertumpuk, satu baris per kedalaman yang terpecahkan.
 *
 * Kolomnya digambar pada sumbu kedalaman, bukan pada nomor cuplikan, dan
 * digerakkan dengan tangan yang sama dengan lembar log dan lembar korelasi:
 * roda memperbesar di sekitar kursor, mistar diseret untuk meregang, dan
 * sapuan tegak memilih interval. Sebelumnya ia sebuah gambar tanpa sumbu —
 * empat ratus pita yang tidak bisa ditunjuk kedalamannya, dan karena itu tidak
 * bisa dijajarkan dengan apa pun di layar sebelahnya.
 *
 * Run yang lebih tua tidak membawa `md`. Kolomnya tetap digambar untuk mereka,
 * dengan pita yang dibagi rata dan tanpa mistar: sebuah sumbu kedalaman yang
 * dikarang dari nomor cuplikan akan mencetak angka yang terbaca sebagai meter
 * dan bukan meter.
 */
function VolumetricTrack({ run, model }: { run: AnalysisRun; model: MineralModel }) {
  const rows = run.qc?.volumes ?? [];
  const incoherence = run.qc?.incoherence ?? [];

  /**
   * Kedalaman tiap baris pita.
   *
   * `volumes_md` dibuat justru untuk ini: ia dijarangkan dengan pilihan
   * cuplikan yang sama dengan pitanya. Run yang lebih tua hanya membawa `md`,
   * yang dijarangkan ke target kurva dan karena itu lebih panjang — di sana
   * baris ke-i diambil dari tempat yang sepadan sepanjang larik itu, yang
   * meleset paling jauh setengah cuplikan dan tidak pernah meleset ratusan
   * meter sebagaimana memasangkannya menurut nomor urut.
   */
  const depths = useMemo(() => {
    if (rows.length < 2) return null;
    const finite = (values: (number | null)[]) =>
      values.every((value): value is number => value !== null && Number.isFinite(value));

    const own = run.qc?.volumes_md ?? [];
    if (own.length === rows.length && finite(own)) return own as number[];

    const md = run.qc?.md ?? [];
    if (md.length < 2 || !finite(md)) return null;
    const curve = md as number[];
    return rows.map((_, index) => {
      const at = (index / (rows.length - 1)) * (curve.length - 1);
      const lower = Math.floor(at);
      const upper = Math.min(lower + 1, curve.length - 1);
      return curve[lower] + (curve[upper] - curve[lower]) * (at - lower);
    });
  }, [run.qc?.volumes_md, run.qc?.md, rows]);

  const range = useMemo<[number, number] | null>(
    () => (depths ? [depths[0], depths[depths.length - 1]] : null),
    [depths],
  );
  const span = range ? range[1] - range[0] : 0;

  const { attachPane, sheet, scaleMode, setScaleMode, stretch, boxHandlers, box } = useDepthSheet({
    storageKey: "evolog.multimin.scale",
    metres: Math.abs(span),
    range,
  });

  /** Pecahan tinggi lembar tempat sebuah baris duduk, dan setebal apa ia. */
  const place = (index: number): { top: string; height: string } => {
    if (!depths || !span) {
      const height = 100 / Math.max(rows.length, 1);
      return { top: `${index * height}%`, height: `${height}%` };
    }
    // Tebalnya adalah jarak ke cuplikan berikutnya, bukan sepersekian jumlah
    // baris: cuplikan yang jaraknya tidak sama rata — dan sesudah penjarangan
    // ia memang tidak selalu sama rata — akan menumpuk atau menyisakan celah.
    const next = index + 1 < depths.length ? depths[index + 1] : depths[index];
    const thickness = index + 1 < depths.length ? next - depths[index] : span / (depths.length - 1);
    return {
      top: `${((depths[index] - depths[0]) / span) * 100}%`,
      height: `${Math.max((thickness / span) * 100, 0.05)}%`,
    };
  };

  /**
   * Tegaknya mengikuti kedalaman ketika ada, supaya kurva inkoherensi dan pita
   * di sebelahnya membaca kedalaman yang sama pada tinggi yang sama. Sumbu
   * kedalamannya `md` dan bukan `depths`: yang terakhir itu milik pita, yang
   * dijarangkan ke cacah baris yang lain — lihat [incoherenceTrace].
   */
  const trace = useMemo(
    () => incoherenceTrace(incoherence, run.qc?.md, range),
    [incoherence, run.qc?.md, range],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <span style={{ font: "600 10.5px var(--font-sans)", color: "var(--ink)" }}>Volumetrics</span>
        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
          0 → 1{depths ? ` · 1:${sheet.scale}` : ""}
        </span>
        {/* Roda memperbesar, dan tanpa jalan pulang sebuah lembar yang sudah
            diperbesar adalah perangkap. Tombolnya hanya muncul ketika ada yang
            perlu dipulangkan. */}
        {depths && scaleMode !== "fit" && (
          <Button variant="ghost" onClick={() => setScaleMode("fit")} title="Back to one screen">
            Fit
          </Button>
        )}
      </div>
      <div
        ref={attachPane}
        className="scroll-y"
        style={{
          height: VOLUMETRIC_PANE_H,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 6,
            width: "100%",
            alignItems: "stretch",
            height: sheet.fitted ? "100%" : sheet.heightPx,
          }}
        >
          {range && (
            <DepthGutter
              top={range[0]}
              base={range[1]}
              ticks={depthTickCount(sheet.heightPx)}
              width={VOLUMETRIC_GUTTER_W}
              // Satu desimal, bukan meter bulat: interval yang dipecahkan
              // solver panjangnya belasan meter, dan pada 1:20 sebuah mistar
              // yang membulatkan mencetak angka yang sama dua kali berturutan.
              label={(depth) => depth.toFixed(1)}
              onStretch={stretch}
              style={{ position: "sticky", left: 0, flex: "none" }}
            />
          )}
          {/* Kedua kolom dikurung bersama: seret di sini memilih interval, dan
              seret di mistar meregang — dua arti yang harus dipisahkan oleh
              elemen, bukan oleh pemeriksaan sasaran kejadian. */}
          <div {...boxHandlers} style={{ position: "relative", flex: 1, minWidth: 0, display: "flex", gap: 6 }}>
            <div
              style={{
                position: "relative",
                flex: 1.25,
                minWidth: 0,
                background: "var(--canvas-deep)",
                border: "1px solid var(--line-strong)",
                borderRadius: "var(--radius)",
                overflow: "hidden",
              }}
            >
              {rows.map((row, index) => {
                const slot = place(index);
                return (
                  <div
                    key={index}
                    style={{ position: "absolute", left: 0, right: 0, top: slot.top, height: slot.height, display: "flex" }}
                  >
                    {row.map((value, componentIndex) => (
                      <span
                        key={componentIndex}
                        style={{
                          width: `${(value ?? 0) * 100}%`,
                          background: model.components[componentIndex]?.color ?? "var(--ink-6)",
                        }}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
            <div
              style={{
                position: "relative",
                flex: 0.75,
                minWidth: 0,
                background: "var(--canvas-deep)",
                border: "1px solid var(--line-strong)",
                borderRadius: "var(--radius)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage:
                    "repeating-linear-gradient(to right, var(--grid-line-strong) 0 1px, transparent 1px 25%)",
                }}
              />
              <svg
                viewBox="0 0 100 1000"
                preserveAspectRatio="none"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                aria-label="Incoherence curve"
              >
                <path d={trace} fill="none" stroke="var(--amber)" strokeWidth={1.2} vectorEffect="non-scaling-stroke" />
              </svg>
              <span
                style={{
                  position: "absolute",
                  left: 5,
                  top: 4,
                  font: "400 9px var(--font-mono)",
                  color: "var(--amber)",
                }}
              >
                incoherence
              </span>
            </div>
            {box && range && (
              <BoxZoomOverlay
                from={box.from}
                to={box.to}
                height={sheet.heightPx}
                top={range[0]}
                base={range[1]}
                format={(depth) => depth.toFixed(1)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Measured vs forward-modelled log — the reconstruction QC. */
/**
 * Sisa tiap alat sepanjang interval, satu baris per alat.
 *
 * Incoherence mengatakan bahwa modelnya tidak menjelaskan lapisan ini. Ia
 * tidak mengatakan alat mana yang tidak dijelaskannya, dan itulah yang
 * membedakan dua keluhan yang sama sekali berbeda: sebuah model mineral yang
 * keliru meleset di beberapa alat sekaligus, sedangkan satu kurva yang rusak
 * meleset sendirian. Keduanya menaikkan incoherence dengan cara yang persis
 * sama, dan hanya tabel ini yang memisahkannya.
 *
 * Angkanya dalam satuan alatnya sendiri di kolom kiri — g/cc, ohm·m — dan
 * dalam sigma di kolom kanan. Yang pertama untuk mengenali besarnya; yang
 * kedua supaya keenam barisnya bisa dibandingkan dalam satu tatapan.
 */
function ResidualPanel({ run }: { run: AnalysisRun }) {
  const residuals = run.qc?.residuals;
  const sigma = run.qc?.solver?.uncertainties ?? {};
  const rows = useMemo(() => {
    if (!residuals) return [];
    return Object.entries(residuals)
      .map(([tool, values]) => {
        const rms = rmsResidual(values);
        const scale = sigma[tool];
        return {
          tool,
          rms,
          sigmas: rms !== null && scale ? rms / scale : null,
        };
      })
      .sort((a, b) => (b.sigmas ?? 0) - (a.sigmas ?? 0));
  }, [residuals, sigma]);

  if (rows.length === 0) return null;

  return (
    <Panel title="Residual per tool (RMS)">
      {rows.map((row) => (
        <div key={row.tool} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ font: "500 10.5px var(--font-mono)", color: "var(--ink-3)", flex: "none" }}>
            {row.tool}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ font: "400 10.5px var(--font-mono)", color: "var(--ink-5)" }}>
            {row.rms === null ? "—" : formatResidual(row.rms)}
          </span>
          <span
            style={{
              font: "500 10.5px var(--font-mono)",
              width: 52,
              textAlign: "right",
              flex: "none",
              // Di atas satu sigma, alat ini tidak dijelaskan modelnya.
              color: (row.sigmas ?? 0) >= 1 ? "var(--amber)" : "var(--ink-4)",
            }}
          >
            {row.sigmas === null ? "—" : `${row.sigmas.toFixed(2)} σ`}
          </span>
        </div>
      ))}
    </Panel>
  );
}

/** Sisa dalam satuan alatnya: cukup angka penting untuk dibedakan dari nol. */
function formatResidual(value: number): string {
  const size = Math.abs(value);
  if (size === 0) return "0";
  if (size < 0.01) return value.toExponential(1);
  return value.toFixed(size < 1 ? 3 : 2);
}

function ReconstructionChart({ run }: { run: AnalysisRun }) {
  const reconstruction = run.qc?.reconstruction ?? {};
  const tool = "RHOB" in reconstruction ? "RHOB" : Object.keys(reconstruction)[0];
  const series = tool ? reconstruction[tool] : null;

  const paths = useMemo(() => {
    if (!series) return null;
    const all = [...series.measured, ...series.reconstructed].filter(
      (v): v is number => v !== null && Number.isFinite(v),
    );
    if (!all.length) return null;
    const min = Math.min(...all);
    const max = Math.max(...all);
    const span = max - min || 1;
    const build = (values: (number | null)[]) =>
      values
        .map((value, index) => {
          if (value === null || !Number.isFinite(value)) return null;
          const x = (index / Math.max(values.length - 1, 1)) * 1000;
          const y = 100 - ((value - min) / span) * 92 - 4;
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .filter((point): point is string => point !== null)
        .reduce((path, point, index) => path + (index === 0 ? `M${point}` : ` L${point}`), "");
    return { measured: build(series.measured), reconstructed: build(series.reconstructed) };
  }, [series]);

  if (!series || !paths) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: "none" }}>
      <span style={{ font: "600 10.5px var(--font-sans)", color: "var(--ink)" }}>Reconstructed {tool}</span>
      <div
        style={{
          position: "relative",
          height: 104,
          background: "var(--canvas-deep)",
          border: "1px solid var(--line-strong)",
          borderRadius: "var(--radius)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "repeating-linear-gradient(to bottom, var(--grid-line-strong) 0 1px, transparent 1px 26px)",
          }}
        />
        <svg
          viewBox="0 0 1000 100"
          preserveAspectRatio="none"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          aria-label={`Reconstruction of ${tool}`}
        >
          <path d={paths.measured} fill="none" stroke="var(--ink)" strokeWidth={1.3} vectorEffect="non-scaling-stroke" />
          <path
            d={paths.reconstructed}
            fill="none"
            stroke="var(--rose)"
            strokeWidth={1.3}
            strokeDasharray="5 3"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        <div
          style={{
            position: "absolute",
            right: 6,
            top: 5,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <LegendLine color="var(--ink)" label="measured" />
          <LegendLine color="var(--rose)" label="reconstructed" dashed />
        </div>
      </div>
    </div>
  );
}

function LegendLine({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 12, height: 0, borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}` }} />
      <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-3)" }}>{label}</span>
    </div>
  );
}

function BatchPanel({
  projectId,
  wells,
  selection,
  onToggle,
  onSelect,
  onRun,
  running,
  mayCompute,
  results,
  parameterSet,
}: {
  projectId: number;
  wells: Well[];
  selection: Set<number>;
  onToggle: (id: number) => void;
  /** Memasang seluruh pilihan sekaligus — dipakai tombol jaring radius. */
  onSelect: (ids: number[]) => void;
  onRun: () => void;
  running: boolean;
  mayCompute: boolean;
  results: { well: string; status: string; net_pay?: number | null; detail?: string }[] | null;
  parameterSet: ParameterSet | null;
}) {
  // Daftar pilih sumur. Pada lapangan bernama panjang — "35/11-13" masih muat,
  // "Merbau North Flank 12ST2" tidak — lebarnyalah yang menentukan apakah
  // sumur yang hendak dicentang bisa dibedakan dari tetangganya.
  const [batchWidth, setBatchWidth] = useStored("evolog.analysis.batch", 322);
  // Sumur dicentang sekali lalu batch-nya berjalan; yang dibaca sesudahnya
  // adalah tabel hasilnya, dan tabel itulah yang paling ingin lebar.
  const [collapsed, setCollapsed] = useStored("evolog.analysis.batch.collapsed", false);

  // Jaring radius. Di sini ia **memilih sumur** dan bukan memotong kedalaman:
  // alur deterministik berjalan atas seluruh sumur, dan sebuah sumur bisa
  // terjaring pada lebih dari satu pita. Pitanya tetap dipajang sebagai
  // keterangan bagi yang memilih — bukan sebagai janji bahwa hitungannya akan
  // dibatasi ke situ, dan panelnya mengatakan itu dengan kalimatnya sendiri.
  const [centerWell, setCenterWell] = useStored<number | null>(
    "evolog.analysis.batch.center", null);
  const [radiusKm, setRadiusKm] = useStored("evolog.analysis.batch.radius.km", 5);
  const [radiusMode, setRadiusMode] = useStored<"surface" | "trajectory">(
    "evolog.analysis.batch.radius.mode", "trajectory");

  const screening = useAsync(
    (signal) =>
      api.get<Screening>(
        `/api/projects/${projectId}/screening/`,
        {
          center_well: centerWell,
          radius: Math.round(radiusKm * 1000),
          radius_mode: radiusMode,
        },
        signal,
      ),
    [projectId, centerWell, radiusKm, radiusMode],
    { skip: centerWell === null },
  );

  // Keadaan tiap sumur, untuk menandai daftar centangnya. Yang tidak terjaring
  // tetap berdiri di daftar dan tetap bisa dicentang tangan: satu putaran
  // radius bisa mengembalikannya, dan daftar yang menyusut menyembunyikan
  // sebabnya.
  const keadaan = useMemo(() => {
    const rows = new Map<number, ScreenedWell>();
    for (const row of screening.data?.wells ?? []) rows.set(row.well_id, row);
    return rows;
  }, [screening.data]);
  const terjaring = useMemo(
    () => (screening.data?.wells ?? [])
      .filter((row) => row.state !== "outside radius" && row.state !== "no coordinates")
      .map((row) => row.well_id),
    [screening.data],
  );

  return (
    <div style={{ display: "flex", alignItems: "stretch", flex: 1, minHeight: 0 }}>
      {collapsed ? (
        <RailStrip
          side="left"
          tabs={BATCH_TABS}
          active="wells"
          onSelect={() => setCollapsed(false)}
          expandLabel="Show the well list"
        />
      ) : (
        <aside
          style={{
            width: batchWidth,
            flex: "none",
            background: "var(--rail)",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Kepala rel: satu-satunya baris di sini yang tidak ikut bergulir,
              sebab daftar sumur pada proyek 61 sumur menghanyutkan apa pun yang
              duduk di atasnya. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "5px 7px 5px 13px",
              borderBottom: "1px solid var(--line-soft)",
              flex: "none",
            }}
          >
            <span className="label">Select wells</span>
            <RailCollapseButton
              side="left"
              collapsed={false}
              label="Hide the well list"
              onClick={() => setCollapsed(true)}
            />
          </div>
          <div
            className="scroll-y"
            style={{
              flex: 1,
              padding: "12px 13px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {/* Jaring radius. Ia menandai daftar di bawahnya dan tidak
                mencentang apa pun sendiri: satu putaran radius bisa
                mengembalikan sumur yang barusan keluar, dan pilihan tangan yang
                ditimpa diam-diam adalah pekerjaan yang hilang tanpa jejak. */}
            <Panel title="Radius filter">
              <Checkbox
                checked={centerWell !== null}
                label="Filter wells by radius"
                onChange={(on) => setCenterWell(on ? (centerWell ?? wells[0]?.id ?? null) : null)}
              />
              {centerWell !== null && (
                <>
                  <Dropdown
                    label="PUSAT"
                    value={centerWell}
                    options={wells.map((well) => ({ value: well.id, label: well.name }))}
                    onChange={setCenterWell}
                  />
                  <NumberField
                    label="Radius"
                    unit="km"
                    value={radiusKm}
                    min={0.01}
                    step={0.5}
                    onChange={setRadiusKm}
                  />
                  <Segmented
                    value={radiusMode}
                    size="sm"
                    options={[
                      { value: "surface", label: "Kepala sumur" },
                      { value: "trajectory", label: "Ikut lintasan" },
                    ]}
                    onChange={(next) => setRadiusMode(next as "surface" | "trajectory")}
                  />
                  {/* Yang membedakan panel ini dari kembarannya di Crossplot:
                      di sana pita memotong sampel, di sini tidak memotong apa
                      pun. Alur deterministik berjalan atas seluruh sumur, dan
                      tabel yang memajang pita tanpa mengatakan ini akan terbaca
                      sebagai janji bahwa hitungannya dibatasi ke situ. */}
                  <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                    This filter picks wells; it does not cut depth. The deterministic
                    workflow still runs over the whole depth of every well that is
                    ticked. The bands are a note for whoever picks.
                  </span>
                  {screening.error && <Notice tone="error">{screening.error}</Notice>}
                  {screening.data?.note && <Notice tone="warn">{screening.data.note}</Notice>}
                  <Button
                    full
                    disabled={!terjaring.length}
                    onClick={() => onSelect(terjaring)}
                  >
                    {terjaring.length
                      ? `Tick the ${terjaring.length} that were caught`
                      : "Nothing was caught"}
                  </Button>
                </>
              )}
            </Panel>

            {wells.map((well) => {
              const hasCurves = well.curve_sets.some((set) => set.curve_count > 0);
              const jaring: ScreenedWell | undefined = keadaan.get(well.id);
              const diluar =
                jaring && (jaring.state === "outside radius" || jaring.state === "no coordinates");
              return (
                <div key={well.id} style={{ opacity: diluar ? 0.45 : 1 }}>
                  <Checkbox
                    checked={selection.has(well.id)}
                    onChange={() => onToggle(well.id)}
                    label={well.name}
                    dot={well.symbol_color === "none" ? "var(--ink-6)" : well.symbol_color}
                    // "no curves" untuk sumur rencana berbunyi seperti LAS yang belum
                    // diimpor, padahal batch-nya akan gagal dengan alasan yang lain.
                    //
                    // Keadaan jaring berdiri di depan keterangan kurvanya, sebab
                    // ia yang menjawab pertanyaan yang sedang ditanyakan orang
                    // ketika jaringnya menyala. Sumur di luar jaring diredupkan
                    // dan tetap bisa dicentang tangan.
                    meta={
                      (jaring ? `${jaring.state} · ` : "") +
                      (hasCurves
                        ? `${well.curve_sets.reduce((n, s) => n + s.curve_count, 0)} curves`
                        : well.status === "planned"
                          ? "planned well"
                          : "no curves")
                    }
                  />
                </div>
              );
            })}
            {screening.data && screening.data.wells.length > 0 && (
              <Panel
                title={`Caught — ${screening.data.kept} of ${screening.data.asked} wells`}
                meta={`${radiusKm} km from ${screening.data.center_name}`}
              >
                <ScreeningTable screening={screening.data} />
              </Panel>
            )}

            <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
              {parameterSet && (
                <KeyValue k="Parameter set" v={`${parameterSet.name} v${parameterSet.version}`} mono={false} />
              )}
              <Button
                variant="primary"
                full
                busy={running}
                disabled={!mayCompute || selection.size === 0}
                onClick={onRun}
              >
                Run {selection.size} wells
              </Button>
              {!mayCompute && <Notice>Your role cannot run computations.</Notice>}
            </div>
          </div>
        </aside>
      )}
      {!collapsed && (
        <Resizer
          width={batchWidth}
          onChange={setBatchWidth}
          min={240}
          max={520}
          side="left"
          label="Resize the well list"
        />
      )}

      <section style={{ flex: 1, minWidth: 0, padding: 16 }}>
        {!results && (
          <Empty>
            {/* Panelnya disebut namanya, bukan tempatnya: sejak rel ini bisa
                dilipat, "on the left" menunjuk ke sebuah pita selebar 30
                piksel, dan sebuah petunjuk yang menunjuk ke tempat kosong
                lebih buruk daripada tidak ada petunjuk sama sekali. */}
            Pick the wells in the Select wells panel, then run the deterministic workflow over all
            of them at once.
            <span style={{ font: "400 11px var(--font-mono)" }}>
              A failure in one well does not stop the others.
            </span>
          </Empty>
        )}
        {results && (
          <Panel title="Batch results" flush bodyStyle={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--panel-header)" }}>
                  {["Well", "Status", "Net pay", "Note"].map((head, index) => (
                    <th
                      key={head}
                      style={{
                        padding: "8px 11px",
                        textAlign: index === 2 ? "right" : "left",
                        font: "600 9.5px var(--font-mono)",
                        color: "var(--ink-2)",
                        letterSpacing: ".06em",
                        borderBottom: "1px solid var(--line-strong)",
                      }}
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((row) => (
                  <tr key={row.well}>
                    <td style={{ padding: "8px 11px", borderBottom: "1px solid var(--line-soft)" }}>
                      <span style={{ font: "500 11.5px var(--font-sans)", color: "var(--ink)" }}>{row.well}</span>
                    </td>
                    <td style={{ padding: "8px 11px", borderBottom: "1px solid var(--line-soft)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                        <StatusDot tone={row.status === "done" ? "ok" : "error"} />
                        <span style={{ font: "400 11px var(--font-sans)", color: "var(--ink-3)" }}>
                          {row.status === "done" ? "done" : "failed"}
                        </span>
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "8px 11px",
                        textAlign: "right",
                        borderBottom: "1px solid var(--line-soft)",
                        font: "500 11px var(--font-mono)",
                        color: "var(--ink)",
                      }}
                    >
                      {row.net_pay != null ? `${row.net_pay.toFixed(1)} m` : "—"}
                    </td>
                    <td
                      style={{
                        padding: "8px 11px",
                        borderBottom: "1px solid var(--line-soft)",
                        font: "400 10.5px var(--font-sans)",
                        color: "var(--ink-5)",
                      }}
                    >
                      {row.detail ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}
      </section>
    </div>
  );
}

/* ------------------------------------------------------------ Lumping */

type LumpBy = "zone" | "well" | "detail";

/** A number or a dash — zero and "none" must never look the same. */
function num(value: number | null | undefined, digits: number): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? "—"
    : value.toFixed(digits);
}

/**
 * The lumping table — reservoir totals summed across wells and zones.
 *
 * Every run summary already carries a per-zone row for one well; what is
 * missing is the field-wide sum. The averages are weighted on the server (PHIE
 * by net pay, SW by pore volume, VSH by net reservoir) rather than being
 * simply averaged — that is the difference between a number you can use and one
 * kelihatan benar.
 */
function LumpingPanel({ projectId }: { projectId: number }) {
  const [by, setBy] = useState<LumpBy>("zone");
  // Sumber angkanya: alur deterministik, alur multi-mineral, atau keduanya
  // side by side. The comparison is computed only over wells carrying both
  // workflows — without that the difference measures wells never run, not
  // perbedaan metodenya.
  const [source, setSource] = useState<"deterministic" | "multimin" | "compare">(
    "deterministic");
  const lump = useAsync(
    (signal) => api.get<LumpingPayload>(
      `/api/projects/${projectId}/lumping/`, { kind: source }, signal),
    [projectId, source],
  );

  const data = lump.data;
  /**
   * Apakah yang tergambar di bawah adalah perbandingan.
   *
   * Bentuk muatannya yang menjawab, bukan `source`: muatan lama masih terpasang
   * satu render sesudah sumbernya berganti, dan aturan yang sama sudah dipakai
   * bagian yang menggambar tabelnya maupun yang mengunduhnya. Ketiganya harus
   * sepakat, kalau tidak pemilihnya lenyap satu render sebelum tabel yang
   * dikemudikannya.
   */
  const membandingkan = !!data && "delta" in data;
  const groups: LumpGroup[] =
    by === "zone" ? (data?.by_zone ?? []) : by === "well" ? (data?.by_well ?? []) : [];

  const unduh = (body: string[], nama: string) => {
    const url = URL.createObjectURL(new Blob([body.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = nama;
    a.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Yang diunduh adalah tabel yang sedang tampak, termasuk ketika itu perbandingan.
   *
   * Muatan `kind=compare` berbentuk lain sama sekali — tidak punya `by_zone`,
   * `rows`, maupun `total` — sedangkan `api.get<LumpingPayload>` adalah
   * penegasan tanpa pemeriksaan, jadi TypeScript meluluskan `data.total.name`
   * atas muatan yang tidak memilikinya. Tombolnya karena itu tampak hidup lalu
   * melempar `Cannot read properties of undefined` dan tidak mengunduh apa pun:
   * sebuah kegagalan yang dari layar terbaca persis seperti tombol yang mati.
   *
   * Bentuknya yang memilih cabang, bukan `source` — muatan lama masih terpasang
   * satu render sesudah sumbernya berganti, dan itu aturan yang sama dengan
   * yang dipakai bagian yang menggambarnya.
   */
  const csv = () => {
    if (!data) return;
    if ("delta" in data) {
      const banding = data as unknown as LumpCompare;
      const kunci = ["net_pay", "ntg", "phie_avg", "sw_avg", "vsh_avg", "hcpv"];
      const baris = (zona: string, alur: string, v: Record<string, number | null>) =>
        [zona, alur, ...kunci.map((k) => v[k] ?? "")].join(",");
      unduh(
        [["zone", "workflow", "net_pay_m", "ntg", "phie", "sw", "vsh", "hcpv_m"].join(","),
         ...banding.delta.flatMap((r) => [
           baris(r.name, "deterministic", r.deterministic),
           baris(r.name, "multimin", r.multimin),
           baris(r.name, "difference", r.delta),
         ])],
        "lumping-compare.csv",
      );
      return;
    }
    const head = ["kelompok", "n", "gross_m", "net_res_m", "net_pay_m", "ntg", "phie", "sw", "vsh", "hcpv_m"];
    const line = (r: LumpGroup) =>
      [r.name, r.count, r.gross, r.net_reservoir, r.net_pay, r.ntg ?? "", r.phie_avg ?? "",
       r.sw_avg ?? "", r.vsh_avg ?? "", r.hcpv].join(",");
    const body = by === "detail"
      ? [["wells", "zones", "gross_m", "net_res_m", "net_pay_m", "ntg", "phie", "sw", "vsh", "hcpv_m"].join(","),
         ...data.rows.map((r) => [r.well, r.zone, r.gross, r.net_reservoir, r.net_pay,
           r.ntg ?? "", r.phie_avg ?? "", r.sw_avg ?? "", r.vsh_avg ?? "", r.hcpv].join(","))]
      : [head.join(","), ...groups.map(line), line(data.total)];
    unduh(body, `lumping-${by}.csv`);
  };

  const cell: React.CSSProperties = {
    padding: "5px 9px",
    textAlign: "right",
    font: "400 10.5px var(--font-mono)",
    borderBottom: "1px solid var(--line-soft)",
    whiteSpace: "nowrap",
  };
  const head: React.CSSProperties = {
    ...cell,
    font: "600 9.5px var(--font-mono)",
    color: "var(--ink-5)",
    borderBottom: "1px solid var(--line-strong)",
    position: "sticky",
    top: 0,
    background: "var(--panel)",
  };
  const left: React.CSSProperties = { textAlign: "left" };

  const kolom = ["Gross m", "Net res. m", "Net pay m", "N/G", "PHIE", "SW", "VSH", "HCPV m"];
  const values = (r: LumpGroup | LumpRow) => [
    num(r.gross, 1), num(r.net_reservoir, 1), num(r.net_pay, 1), num(r.ntg, 3),
    num(r.phie_avg, 3), num(r.sw_avg, 3), num(r.vsh_avg, 3), num(r.hcpv, 2),
  ];

  return (
    <div className="scroll-y" style={{ flex: 1, minHeight: 0, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <Segmented
          value={source}
          options={[
            { value: "deterministic", label: "Deterministic" },
            { value: "multimin", label: "Multi-mineral" },
            { value: "compare", label: "Compare" },
          ]}
          onChange={(value) => setSource(value as typeof source)}
        />
        {/* Perbandingannya hanya dilumpat menurut zona — `_delta` tidak
            mengenal pengelompokan lain — jadi ketiga pilihan ini dulu
            menggambar tabel yang sama persis. Sebuah pemilih yang tidak
            memilih apa pun lebih buruk daripada tidak ada pemilih: ia
            membuat orang mencari perbedaan yang tidak pernah ada.
            `by` sendiri tidak disetel ulang, sehingga pilihan yang tadi
            dipakai kembali apa adanya begitu sumber tunggal dibuka lagi. */}
        {!membandingkan && (
          <Segmented
            value={by}
            options={[
              { value: "zone", label: "By zone" },
              { value: "well", label: "By well" },
              { value: "detail", label: "Detail" },
            ]}
            onChange={setBy}
          />
        )}
        <span style={{ flex: 1 }} />
        {data && (
          <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
            {data.wells_evaluated} of {data.wells_total} wells evaluated
          </span>
        )}
        <Button variant="ghost" onClick={csv} disabled={!data}>
          Download CSV
        </Button>
      </div>

      {lump.loading && <Loading label="Summing the totals…" />}
      {lump.error && <Empty>The lumping table could not be loaded: {String(lump.error)}</Empty>}

      {membandingkan && (
        <CompareLumping data={data as unknown as LumpCompare} />
      )}

      {data && !membandingkan && (
        <>
          <div style={{ border: "1px solid var(--line)", borderRadius: 7, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--panel-inset)" }}>
              <thead>
                <tr>
                  <th style={{ ...head, ...left }}>{by === "detail" ? "Well" : by === "zone" ? "Zone" : "Well"}</th>
                  {by === "detail" && <th style={{ ...head, ...left }}>Zone</th>}
                  {by !== "detail" && <th style={head}>n</th>}
                  {kolom.map((k) => (
                    <th key={k} style={head}>{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {by === "detail"
                  ? data.rows.map((r) => (
                      <tr key={`${r.well_id}-${r.zone}`}>
                        <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>{r.well}</td>
                        <td style={{ ...cell, ...left, color: "var(--ink-4)" }}>{r.zone}</td>
                        {values(r).map((v, i) => (
                          <td key={i} style={cell}>{v}</td>
                        ))}
                      </tr>
                    ))
                  : groups.map((g) => (
                      <tr key={g.name}>
                        <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>{g.name}</td>
                        <td style={cell}>{g.count}</td>
                        {values(g).map((v, i) => (
                          <td key={i} style={cell}>{v}</td>
                        ))}
                      </tr>
                    ))}
                <tr>
                  <td
                    style={{
                      ...cell,
                      ...left,
                      font: "600 10.5px var(--font-mono)",
                      borderTop: "1px solid var(--line-strong)",
                    }}
                    colSpan={by === "detail" ? 2 : 1}
                  >
                    Total
                  </td>
                  {by !== "detail" && (
                    <td style={{ ...cell, borderTop: "1px solid var(--line-strong)" }}>
                      {data.total.count}
                    </td>
                  )}
                  {values(data.total).map((v, i) => (
                    <td
                      key={i}
                      style={{ ...cell, font: "600 10.5px var(--font-mono)", borderTop: "1px solid var(--line-strong)" }}
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 5 }}>
            <span style={{ font: "400 10px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
              Thicknesses are summed. PHIE is net-pay weighted, SW pore-volume
              weighted (net pay × PHIE), VSH net-reservoir weighted. HCPV = net pay ×
              PHIE × (1 − SW), the hydrocarbon volume per unit area in metres —
              not reserves: there is no area, no formation volume factor and no
              recovery in it.
            </span>
            {data.wells_missing.length > 0 && (
              <span style={{ font: "400 10px var(--font-sans)", color: "var(--warn-ink)" }}>
                Not evaluated and not included in the sums:{" "}
                {data.wells_missing.join(", ")}.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* --------------------------------------------- Lumping: dua alur berdampingan */

interface LumpCompare {
  deterministic: LumpingPayload;
  multimin: LumpingPayload;
  shared_wells: string[];
  /** Sumur yang punya kedua alur, dan seluruh sumur proyek. Keduanya dicetak
   *  kepala tabel yang sama dengan kepala kedua sumber tunggal. */
  wells_evaluated: number;
  wells_total: number;
  delta: {
    name: string;
    wells: number;
    deterministic: Record<string, number | null>;
    multimin: Record<string, number | null>;
    delta: Record<string, number | null>;
  }[];
  note: string;
}

/**
 * The deterministic and multi-mineral workflows compared on the same zones.
 *
 * Barisnya sengaja tiga: nilai kedua alur, lalu selisihnya. Menampilkan
 * the difference alone hides the magnitude — a drop of 0.12 on a porosity of
 * 0.24 and on one of 0.60 are two completely different stories.
 */
function CompareLumping({ data }: { data: LumpCompare }) {
  const cell: React.CSSProperties = {
    padding: "5px 9px", textAlign: "right",
    font: "400 10.5px var(--font-mono)",
    borderBottom: "1px solid var(--line-soft)", whiteSpace: "nowrap",
  };
  const head: React.CSSProperties = {
    ...cell, font: "600 9.5px var(--font-mono)", color: "var(--ink-5)",
    borderBottom: "1px solid var(--line-strong)",
  };
  const left: React.CSSProperties = { textAlign: "left" };
  const kolom = ["net_pay", "ntg", "phie_avg", "sw_avg", "vsh_avg", "hcpv"];
  const judul = ["Net pay m", "N/G", "PHIE", "SW", "VSH", "HCPV m"];
  const digits: Record<string, number> = {
    net_pay: 1, ntg: 3, phie_avg: 3, sw_avg: 3, vsh_avg: 3, hcpv: 2,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ border: "1px solid var(--line)", borderRadius: 7, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--panel-inset)" }}>
          <thead>
            <tr>
              <th style={{ ...head, ...left }}>Zone</th>
              <th style={{ ...head, ...left }}>Workflow</th>
              {judul.map((k) => <th key={k} style={head}>{k}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.delta.map((row) => (
              <Fragment key={row.name}>
                <tr>
                  <td style={{ ...cell, ...left, color: "var(--ink-2)" }} rowSpan={3}>
                    {row.name}
                  </td>
                  <td style={{ ...cell, ...left, color: "var(--ink-4)" }}>Deterministic</td>
                  {kolom.map((k) => (
                    <td key={k} style={cell}>{num(row.deterministic[k], digits[k])}</td>
                  ))}
                </tr>
                <tr>
                  <td style={{ ...cell, ...left, color: "var(--ink-4)" }}>Multi-mineral</td>
                  {kolom.map((k) => (
                    <td key={k} style={cell}>{num(row.multimin[k], digits[k])}</td>
                  ))}
                </tr>
                <tr>
                  <td style={{ ...cell, ...left, font: "600 10px var(--font-mono)" }}>Difference</td>
                  {kolom.map((k) => {
                    const v = row.delta[k];
                    return (
                      <td
                        key={k}
                        style={{
                          ...cell,
                          font: "600 10.5px var(--font-mono)",
                          color: v === null ? "var(--ink-6)"
                            : v < 0 ? "var(--warn-ink)" : "var(--ok-ink)",
                        }}
                      >
                        {v === null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(digits[k])}`}
                      </td>
                    );
                  })}
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <span style={{ font: "400 10px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
        {data.note} Compared over only the {data.shared_wells.length} wells that
        carry both workflows: {data.shared_wells.join(", ")}.
      </span>
    </div>
  );
}
